/**
 * Visible equation tags should be sequential numbers (1), (2), …
 * not raw LML/LaTeX labels like "eq:norm". Labels stay on the block
 * for cross-references; only the display tag is derived here.
 */

export type EquationNumberable = {
  id?: string;
  type?: string;
  content?: Record<string, unknown> | null;
};

/** Whether this equation block should show a margin number. */
export function isEquationNumbered(content: Record<string, unknown> | null | undefined): boolean {
  if (!content) return false;
  const mode = typeof content.equationMode === "string" ? content.equationMode : "display";
  // Inline and starred modes are never numbered.
  if (mode === "inline" || mode.endsWith("*")) return false;
  // Numbered unless the author opted out — as both PDF engines do. Showing
  // no number for an unlabelled equation the PDF numbers made every later
  // number on screen disagree with the printed one.
  return content.numbered !== false;
}

/**
 * 1-based display number for `blockId` among numbered equations in `blocks`,
 * or null if this equation should not show a tag.
 */
export function getEquationDisplayNumber(
  blocks: readonly EquationNumberable[],
  blockId: string | undefined | null,
): number | null {
  if (!blockId) return null;
  let n = 0;
  for (const b of blocks) {
    if (b.type !== "equation") continue;
    if (!isEquationNumbered(b.content ?? undefined)) continue;
    n += 1;
    if (b.id === blockId) return n;
  }
  return null;
}
